import React, { useContext, PropsWithChildren } from 'react';
import styled from '@emotion/styled';
import Box from '@mui/material/Box';
import { gql } from '@yi/core';
import { i18n_keep_up_the_great_work } from '@yi/i18n/phrases/i18n_keep_up_the_great_work';
import { i18n_logged_class_1 } from '@yi/i18n/phrases/i18n_logged_class_1';
import { i18n_logged_class_2 } from '@yi/i18n/phrases/i18n_logged_class_2';
import { i18n_logged_meditation_1 } from '@yi/i18n/phrases/i18n_logged_meditation_1';
import { i18n_logged_meditation_2 } from '@yi/i18n/phrases/i18n_logged_meditation_2';
import { i18n_number } from '@yi/i18n/phrases/i18n_number';
import { i18n_of_your } from '@yi/i18n/phrases/i18n_of_your';
import { i18n_this_week } from '@yi/i18n/phrases/i18n_this_week';
import { i18n_way_to_go } from '@yi/i18n/phrases/i18n_way_to_go';
import { i18n_you_just_finished } from '@yi/i18n/phrases/i18n_you_just_finished';

import { useCurrentUser } from '~/src/common/user';
import { turquoise, white9, playlistHeight, expandedPlaylistHeight } from '~/styles';

import { CurrentUserAvatar } from '../CurrentUserAvatar';
import { useLocale } from '../LocaleProvider';
import { PlaylistContext } from '../PlayListPlayer';
import { Snackbar } from '../Snackbar';

interface Props {
  show: boolean;
  contentType:
    | gql.typesClient.LogConsumptionTrackingType.MEDITATION
    | gql.typesClient.LogConsumptionTrackingType.CLASS
    | gql.typesClient.LogConsumptionTrackingType.LIVE;
  onClose(): void;
}

const Title = styled.div`
  color: ${turquoise};
  font-weight: 600;
`;

const EncouragementText = styled.div`
  color: ${white9};
  padding-top: 8px;
`;

export const CompletionSnackbar: React.FC<PropsWithChildren<Props>> = ({
  show,
  onClose,
  contentType,
}) => {
  const { locale } = useLocale();

  const { showDetailView, playlistEnabled } = useContext(PlaylistContext);
  const { currentUser } = useCurrentUser();
  const userGoalCompleted =
    contentType === 'CLASS'
      ? currentUser?.class_count_current_week
      : currentUser?.meditation_count_current_week;

  const userGoal = contentType === 'CLASS' ? currentUser?.class_goal : currentUser?.meditation_goal;

  const userGoalCompletedDisplayValue = userGoalCompleted || 1; // assume undefined is same as 0

  const userMeditationDisplayCount =
    contentType === 'MEDITATION'
      ? userGoalCompletedDisplayValue
      : currentUser?.meditation_count_current_week;
  const userClassDisplayCount =
    contentType === 'CLASS' ? userGoalCompletedDisplayValue : currentUser?.class_count_current_week;

  const textFragment1 =
    contentType === 'CLASS'
      ? i18n_logged_class_1(locale)
      : contentType === 'MEDITATION'
      ? i18n_logged_meditation_1(locale)
      : '';
  const textFragment2 =
    contentType === 'CLASS'
      ? i18n_logged_class_2(locale)
      : contentType === 'MEDITATION'
      ? i18n_logged_meditation_2(locale)
      : '';

  // @TODO i18n use phrase variable
  const encouragementText = `${i18n_you_just_finished(locale)} ${textFragment1} ${i18n_number(
    locale,
  )} ${userGoalCompletedDisplayValue} ${i18n_of_your(
    locale,
  )} ${userGoal} ${textFragment2} ${i18n_this_week(locale)} ${i18n_keep_up_the_great_work(locale)}`;

  if (!currentUser || !userGoal) return null;

  return (
    <Box width={{ xs: '280px', sm: '320px', md: '450px' }}>
      <Snackbar
        alignRightMobile
        style={{
          width: 'inherit',
          marginBottom: playlistEnabled
            ? showDetailView
              ? expandedPlaylistHeight
              : playlistHeight
            : 'unset',
        }}
        open={show}
        setClose={onClose}
      >
        <div data-testid="completion-snackbar">
          <Title>{i18n_way_to_go(locale)}</Title>
          <CurrentUserAvatar
            fontSize="30px"
            width={46}
            forcedMeditationRingValue={userMeditationDisplayCount}
            forcedClassRingValue={userClassDisplayCount}
          />
          <EncouragementText>{encouragementText}</EncouragementText>
        </div>
      </Snackbar>
    </Box>
  );
};
